import {
  Component,
  Input,
  forwardRef,
  NgZone,
  inject,
  output,
  ChangeDetectionStrategy
} from '@angular/core';
import { MultiSelect, MultiSelectStyle } from 'primeng/multiselect';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule
} from '@angular/forms';
import Sortable from 'sortablejs';
import type { SortableEvent } from 'sortablejs';
import {
  FilterService,
  OverlayService,
  SelectItem,
  PrimeTemplate
} from 'primeng/api';
import './multiselect-selection.patch';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MTooltipComponent } from '../m-tooltip/m-tooltip.component';
import { EllipsisPipe } from '../../pipes/ellipsis.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-multiselect',
  templateUrl: './m-multiselect.component.html',
  styleUrls: ['./m-multiselect.component.scss'],
  providers: [
    MultiSelectStyle,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MMultiSelectComponent),
      multi: true
    }
  ],
  imports: [
    MultiSelect,
    NgClass,
    FormsModule,
    PrimeTemplate,
    MTooltipComponent,
    NgTemplateOutlet,
    EllipsisPipe
  ]
})
export class MMultiSelectComponent
  extends MultiSelect
  implements ControlValueAccessor
{
  readonly dragEnd = output<SelectItem[]>();

  constructor() {
    const zone = inject(NgZone);
    const filterService = inject(FilterService);
    const overlayService = inject(OverlayService);

    super(zone, filterService, overlayService);
  }

  @Input() isDraggable = false;
  @Input() width: string | undefined;
  @Input() height: string | undefined;
  @Input() isValid = true;
  /** Min flattened option count before the large-list hover optimization engages. */
  @Input() optimizeThreshold = 100;
  readonly LABEL_TRUNCATE_LIMIT = 20;

  /** Latched once the threshold is crossed so the optimization never toggles back off. */
  private hasEngagedOptimization = false;

  /**
   * Latched true once the flattened count crosses `optimizeThreshold`. Drives the
   * `m-multiselect-optimized` panel class and `[focusOnHover]="!isLargeList"`; latched so a list
   * shrinking below the threshold keeps the CSS hover highlight (focus-follows-hover stays off).
   */
  get isLargeList(): boolean {
    if (
      !this.hasEngagedOptimization &&
      this.flattenedOptionCount() >= this.optimizeThreshold
    ) {
      this.hasEngagedOptimization = true;
    }
    return this.hasEngagedOptimization;
  }

  /**
   * Group-aware option count: grouped lists store only group wrappers in `_options()`, so sum
   * each group's children — the raw length would undercount and never cross the threshold.
   */
  private flattenedOptionCount(): number {
    const options = this._options();
    if (!options) {
      return 0;
    }
    if (!this.group) {
      return options.length;
    }
    let count = 0;
    for (const group of options) {
      count += 1 + (this.getOptionGroupChildren(group)?.length ?? 0);
    }
    return count;
  }

  // PrimeNG 20 applies root host class/style + container-click on the host via
  // `host: { '[class]': "cn(cx('root'), styleClass)", '[style]': "sx('root')", '(click)': '...' }`.
  // This wrapper extends MultiSelect but renders its OWN inner <p-multiselect>, so that host
  // metadata is inherited onto <m-multiselect> too — duplicating styling and firing a second
  // container click. Neutralize on the wrapper host; the inner instance is unaffected.
  override cx(): string {
    return '';
  }

  override sx(): { [klass: string]: unknown } | undefined {
    return undefined;
  }

  override onContainerClick(): void {
    // no-op: the inner <p-multiselect> handles its own container click
  }

  // Handle
  handlePanelShow(): void {
    this.initSortable();

    this.preventDropdownCloseOnScroll();

    // Emit the event to support external handlers
    this.onPanelShow.emit();
  }

  private preventDropdownCloseOnScroll(): void {
    const modalContent = document.querySelector('.p-dialog-content');
    if (modalContent) {
      modalContent.addEventListener('scroll', this.stopScrollPropagation, true);
    }
  }

  private stopScrollPropagation = (event: Event) => {
    event.stopPropagation();
  };

  // Initialize sortable drag and drop
  initSortable() {
    if (!this.isDraggable) return;

    const container = document.querySelector('.p-multiselect-list');
    if (container) {
      new Sortable(container as HTMLElement, {
        animation: 100,
        delay: 100,
        draggable: 'p-multiselect-item',
        forceFallback: true,
        touchStartThreshold: 1,
        onClone: () => {
          container.classList.add('dragging-in-progress');
        },
        onEnd: (event: SortableEvent) => {
          const oldIndex = event.oldIndex ?? 0;
          const newIndex = event.newIndex ?? 0;

          this.reorderOptions(oldIndex, newIndex);

          container.classList.remove('dragging-in-progress');
          this.dragEnd.emit(this.options ?? []);
        }
      });
    }
  }

  // Assuming your options array consists of objects with 'label' and 'value' properties
  reorderOptions(oldIndex: number, newIndex: number): void {
    if (!this.options) {
      return;
    }
    const item = this.options.splice(oldIndex, 1)[0];
    this.options.splice(newIndex, 0, item);
  }
}
